/**
 * useIntegrationConnect — the shared "click Connect → run the right
 * connect flow → refetch status when it completes" engine.
 *
 * This is the SINGLE source of truth for "click Connect on provider X →
 * show the right in-place connect UI": per-provider OAuth popups, the
 * GitLab/Lark form modals, the paste-token modals (Figma/Linear/Plane/
 * OpenAI/Anthropic/Cursor admin), a `refreshKey` that bumps on completion,
 * and a window `message` listener that catches the OAuth popup's
 * postMessage. Every surface that needs the same connect/connected
 * affordance — the marketplace detail modal AND the pipeline Settings tab —
 * drives off this one implementation instead of N that drift.
 *
 * EVERY provider connects IN-PLACE (popup or form modal). There is NO
 * full-page `window.location.href` redirect — an unknown provider surfaces
 * an error via `onError` instead of silently navigating away.
 *
 * Returns:
 *   refreshKey        — bump-on-connect counter; pass into your status
 *                       fetch's dep array so a successful Connect refetches.
 *   handleConnectClick(providerId, fallbackPath)
 *                     — dispatches the click to the right IN-PLACE flow:
 *                         github/jira/slack/sentry/notion → OAuth popup
 *                         gitlab                          → GitlabConnectModal
 *                         lark                            → LarkConnectModal
 *                         figma/linear/plane              → paste-token modal
 *                         openai_billing/anthropic_billing/
 *                           cursor_admin                  → admin-key modal
 *                         anything else                   → onError (never a
 *                                                           redirect)
 *                       (`fallbackPath` is accepted for call-site
 *                       compatibility but no longer triggers navigation.)
 *   connectModals     — render this once near your component root; it mounts
 *                       every form/paste-token modal wired to bump refreshKey
 *                       on success.
 *
 * Options:
 *   onError(providerId, error) — called when an OAuth-start mutation fails
 *                       OR an unknown provider is clicked. Surface a toast /
 *                       banner here so Connect is never a silent no-op.
 *
 * Usage:
 *   const { refreshKey, handleConnectClick, connectModals } =
 *     useIntegrationConnect({ onError: (id, err) => showToast(err.message) });
 *   // ...fetch status, include refreshKey in the effect deps...
 *   <button onClick={() => handleConnectClick(id, meta.connectPath)}>Connect</button>
 *   {connectModals}
 */

import React, { useCallback, useEffect, useState } from 'react';
import GitlabConnectModal from '../components/integrations/GitlabConnectModal';
import LarkConnectModal from '../components/integrations/LarkConnectModal';
import FigmaConnectModal from '../components/integrations/FigmaConnectModal';
import LinearConnectModal from '../components/integrations/LinearConnectModal';
import PlaneConnectModal from '../components/integrations/PlaneConnectModal';
import OpenAIBillingConnectModal from '../components/integrations/OpenAIBillingConnectModal';
import AnthropicBillingConnectModal from '../components/integrations/AnthropicBillingConnectModal';
import CursorAdminConnectModal from '../components/integrations/CursorAdminConnectModal';
import { useStartOAuth } from './useIntegrations';

// Marker the popup-done page posts back to the opener after OAuth
// completes. Same constants the marketplace modal uses — kept in sync so
// either surface catches the popup's postMessage.
const OAUTH_POPUP_DONE_PATH = '/oauth-popup-done.html';
const OAUTH_SUCCESS_EVENT = 'zibby:oauth-success';

// Providers whose connect UI is an in-place FORM / paste-token modal (not
// an OAuth popup). Maps provider id → which modal-open flag to flip.
const FORM_MODAL_PROVIDERS = new Set([
  'gitlab',
  'lark',
  'figma',
  'linear',
  'plane',
  'openai_billing',
  'anthropic_billing',
  'cursor_admin',
]);

export default function useIntegrationConnect(options = {}) {
  const { onError } = options;

  // Bumped after any successful Connect (OAuth popup OR a form/paste-token
  // modal submit) so the caller's status fetch re-runs. Cheaper + clearer
  // than threading a refetch callback around.
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Per-provider form/paste-token modal open flags. Each provider that
  // connects via one of OUR modals (rather than an OAuth popup) gets a flag
  // here; handleConnectClick flips the right one.
  const [openModal, setOpenModal] = useState(null); // provider id or null
  const closeModal = useCallback(() => setOpenModal(null), []);

  // Per-provider OAuth-start mutations. Each is a thin wrapper around
  // `GET /<provider>/oauth/start?returnTo=…`; mutate() returns
  // { authUrl } that we open in a popup.
  const startGithubOAuth = useStartOAuth('github');
  const startJiraOAuth = useStartOAuth('jira');
  const startSlackOAuth = useStartOAuth('slack');
  const startSentryOAuth = useStartOAuth('sentry');
  const startNotionOAuth = useStartOAuth('notion');
  const OAUTH_HOOKS = {
    github: startGithubOAuth,
    jira: startJiraOAuth,
    slack: startSlackOAuth,
    sentry: startSentryOAuth,
    notion: startNotionOAuth,
  };

  // Listen for the popup-done page postMessage and refetch status. The
  // popup-done page posts to `window.location.origin`, so cross-origin
  // messages are dropped by the browser regardless of this filter.
  useEffect(() => {
    const onMessage = (e) => {
      if (e?.data?.type === OAUTH_SUCCESS_EVENT) {
        bumpRefresh();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [bumpRefresh]);

  // Dispatch a Connect click to the right IN-PLACE flow per provider. NO
  // provider redirects the page — form/paste-token providers open a modal,
  // OAuth providers open a popup, and an unknown provider surfaces an error
  // (never a silent no-op, never a navigation).
  const handleConnectClick = useCallback((providerId) => {
    if (FORM_MODAL_PROVIDERS.has(providerId)) {
      setOpenModal(providerId);
      return;
    }
    const hook = OAUTH_HOOKS[providerId];
    if (!hook) {
      // Unknown provider — surface an error rather than redirecting or
      // silently doing nothing. (Previously this navigated to a fallback
      // path, which is the inconsistent full-page redirect we removed.)
      const err = new Error(`No connect flow is available for "${providerId}".`);
      if (onError) onError(providerId, err);
      return;
    }
    hook.mutate(
      { returnTo: OAUTH_POPUP_DONE_PATH },
      {
        onSuccess: (data) => {
          // Open the OAuth URL in a small popup so the originating surface
          // stays visible behind it. Popup posts back when done. Fallback
          // to same-tab redirect when the browser blocks the popup.
          const popup = window.open(
            data.authUrl,
            'zibby-oauth',
            'popup=1,width=640,height=720,scrollbars=yes',
          );
          if (!popup) {
            window.location.href = data.authUrl;
          }
        },
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.error(`[integrations] Failed to start ${providerId} OAuth:`, err);
          // Surface to the caller so Connect never silently no-ops (this is
          // the Notion bug: the OAuth-start route can 404/500 and the error
          // was previously swallowed at the console).
          if (onError) onError(providerId, err);
        },
      },
    );
  // OAUTH_HOOKS isn't a stable reference but the mutate functions inside it
  // are (react-query memoizes per service); fine for the intent here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError]);

  // Every form/paste-token Connect modal, pre-wired to bump refreshKey on
  // success. Render once near the consumer's root. IonModal portals to
  // <body> so placement in the tree doesn't matter. GitLab defaults to the
  // self-hosted form (the marketplace + pipeline surfaces don't distinguish
  // cloud vs self-hosted; the dedicated Integrations page passes its own
  // mode).
  const connectModals = (
    <>
      <GitlabConnectModal
        isOpen={openModal === 'gitlab'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
      <LarkConnectModal
        isOpen={openModal === 'lark'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
      <FigmaConnectModal
        isOpen={openModal === 'figma'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
      <LinearConnectModal
        isOpen={openModal === 'linear'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
      <PlaneConnectModal
        isOpen={openModal === 'plane'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
      <OpenAIBillingConnectModal
        isOpen={openModal === 'openai_billing'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
      <AnthropicBillingConnectModal
        isOpen={openModal === 'anthropic_billing'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
      <CursorAdminConnectModal
        isOpen={openModal === 'cursor_admin'}
        onClose={closeModal}
        onConnected={bumpRefresh}
      />
    </>
  );

  return { refreshKey, handleConnectClick, connectModals };
}
